import React, { useEffect, useState } from "react";
import { X, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApi } from "@/lib/api";
import { AdminTeamSummary, AdminTeamRateLimitView, AdminTeamRateLimitListResponse } from "@/lib/types";
import { cn } from "@/lib/utils";

function ModalShell({
  title,
  onClose,
  children,
  maxWidth = "max-w-[480px]",
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: string;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={cn("relative w-full mx-4 sci-card p-6 max-h-[90vh] flex flex-col", maxWidth)}>
        <div className="flex items-center justify-between mb-5 shrink-0">
          <h2 className="text-base font-semibold text-white" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
            {title}
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 min-h-0 pr-1">
          {children}
        </div>
      </div>
    </div>
  );
}

function RateLimitRow({
  teamId,
  row,
  defaults,
  busyId,
  setBusyId,
  onSaved,
}: {
  teamId: string;
  row: AdminTeamRateLimitView;
  defaults: { tpm_limit: number; rpm_limit: number };
  busyId: string | null;
  setBusyId: (id: string | null) => void;
  onSaved: () => void;
}) {
  const [tpmInput, setTpmInput] = useState("");
  const [rpmInput, setRpmInput] = useState("");

  useEffect(() => {
    setTpmInput(row.override_tpm !== null ? row.override_tpm.toString() : "");
    setRpmInput(row.override_rpm !== null ? row.override_rpm.toString() : "");
  }, [row.override_tpm, row.override_rpm]);

  const isBusy = busyId === row.science_llm_id;

  const handleSave = async () => {
    const tpmNum = tpmInput.trim() === "" ? null : Number(tpmInput);
    const rpmNum = rpmInput.trim() === "" ? null : Number(rpmInput);

    if (tpmNum !== null) {
      if (isNaN(tpmNum) || tpmNum < 0) {
        toast.error("Invalid TPM value");
        return;
      }
      if (row.ceiling_tpm !== null && tpmNum > row.ceiling_tpm) {
        toast.error(`TPM exceeds ceiling (${row.ceiling_tpm})`);
        return;
      }
    }

    if (rpmNum !== null) {
      if (isNaN(rpmNum) || rpmNum < 0) {
        toast.error("Invalid RPM value");
        return;
      }
      if (row.ceiling_rpm !== null && rpmNum > row.ceiling_rpm) {
        toast.error(`RPM exceeds ceiling (${row.ceiling_rpm})`);
        return;
      }
    }

    setBusyId(row.science_llm_id);
    try {
      await adminApi.setTeamRateLimit(teamId, row.science_llm_id, {
        tpm_limit: tpmNum,
        rpm_limit: rpmNum,
      });
      toast.success("Rate limit saved");
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save rate limit");
    } finally {
      setBusyId(null);
    }
  };

  const handleReset = async () => {
    setBusyId(row.science_llm_id);
    try {
      await adminApi.deleteTeamRateLimit(teamId, row.science_llm_id);
      toast.success("Rate limit reset to default");
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to reset rate limit");
    } finally {
      setBusyId(null);
    }
  };

  const showBlockedTpm = row.effective_tpm === 0;
  const showBlockedRpm = row.effective_rpm === 0;

  return (
    <div className="py-4 border-b border-white/5 last:border-0 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm font-medium text-slate-200">{row.name}</div>
          <div className="text-[10px] text-slate-500 font-mono mt-0.5">{row.model_key}</div>
        </div>
        <div className="flex items-center gap-2">
          {row.source === "team_override" ? (
            <span className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[10px]">
              Team Override
            </span>
          ) : (
            <span className="px-1.5 py-0.5 rounded bg-white/5 text-slate-400 border border-white/10 text-[10px]">
              Default
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <div className="flex justify-between items-center text-xs">
            <Label className="text-slate-400">TPM</Label>
            <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
              <span title="Ceiling">Max: {row.ceiling_tpm === null ? "∞" : row.ceiling_tpm}</span>
              <span>|</span>
              <span title="Default">Def: {defaults.tpm_limit}</span>
            </div>
          </div>
          <Input
            value={tpmInput}
            onChange={(e) => setTpmInput(e.target.value)}
            disabled={isBusy}
            placeholder="Override..."
            className="h-8 text-xs bg-black/40 border-white/10 focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:border-blue-500"
          />
          <div className="flex items-center justify-between mt-1 text-[10px]">
            <span className={cn(
              "font-mono",
              showBlockedTpm ? "text-red-400" : "text-emerald-400/80"
            )}>
              Eff: {row.effective_tpm}
              {showBlockedTpm && " (Blocked)"}
            </span>
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex justify-between items-center text-xs">
            <Label className="text-slate-400">RPM</Label>
            <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
              <span title="Ceiling">Max: {row.ceiling_rpm === null ? "∞" : row.ceiling_rpm}</span>
              <span>|</span>
              <span title="Default">Def: {defaults.rpm_limit}</span>
            </div>
          </div>
          <Input
            value={rpmInput}
            onChange={(e) => setRpmInput(e.target.value)}
            disabled={isBusy}
            placeholder="Override..."
            className="h-8 text-xs bg-black/40 border-white/10 focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:border-blue-500"
          />
          <div className="flex items-center justify-between mt-1 text-[10px]">
            <span className={cn(
              "font-mono",
              showBlockedRpm ? "text-red-400" : "text-emerald-400/80"
            )}>
              Eff: {row.effective_rpm}
              {showBlockedRpm && " (Blocked)"}
            </span>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-1">
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2.5 text-[11px] bg-transparent border-white/10 text-slate-300 hover:bg-white/5 hover:text-white"
          onClick={handleReset}
          disabled={isBusy || row.source === "default"}
        >
          <RotateCcw size={12} className="mr-1.5" />
          恢复默认
        </Button>
        <Button
          size="sm"
          className="h-7 px-2.5 text-[11px] bg-blue-600 hover:bg-blue-500 text-white border-0"
          onClick={handleSave}
          disabled={isBusy}
        >
          <Save size={12} className="mr-1.5" />
          {isBusy ? "保存中..." : "保存"}
        </Button>
      </div>
    </div>
  );
}

export default function RateLimitsModal({
  team,
  onClose,
}: {
  team: AdminTeamSummary;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AdminTeamRateLimitListResponse | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await adminApi.listTeamRateLimits(team.team.id);
      setData(res);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load rate limits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [team.team.id]);

  return (
    <ModalShell title={`Rate Limits: ${team.team.name}`} onClose={onClose} maxWidth="max-w-[600px]">
      <div className="flex flex-col min-h-[300px]">
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-sm text-slate-500">
            Loading...
          </div>
        ) : !data ? (
          <div className="flex-1 flex items-center justify-center text-sm text-red-400">
            Error loading rate limits.
          </div>
        ) : data.rate_limits.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-sm text-slate-500">
            No models found.
          </div>
        ) : (
          <div className="flex flex-col">
            {data.rate_limits.map((row) => (
              <RateLimitRow
                key={row.science_llm_id}
                teamId={team.team.id}
                row={row}
                defaults={data.defaults}
                busyId={busyId}
                setBusyId={setBusyId}
                onSaved={fetchData}
              />
            ))}
          </div>
        )}
      </div>
    </ModalShell>
  );
}
